/**
 * Per-agent conversation session-id persistence.
 *
 * The agent runtime keys its `SessionMemory` tier ("recent turns in this
 * session") by the `sessionId` sent on each turn. Minting a fresh id on every
 * console mount (the prior behaviour) discarded that continuity on each reload
 * or navigation, so a returning user always resumed against a blank session.
 * We persist the id per agent in localStorage and reuse it across mounts, so
 * server-side session continuity survives reloads exactly as `SessionMemory`
 * expects.
 *
 * Best-effort storage (mirrors `preferences.ts`): a disabled or full store, or
 * a stored value that no longer matches the id contract, degrades to "mint a
 * fresh id this mount" rather than breaking the conversation.
 *
 * SCOPE: this persists the *session id* so server-side continuity survives a
 * reload. Rehydrating the on-screen transcript for the resumed id is handled
 * separately by `useSessions` (it fetches the transcript when the resumed id
 * has stored turns), so a reload restores the visible conversation too.
 *
 * IDLE EXPIRY: continuity-over-reload is meant for "I came back to the same
 * conversation," not "I returned days later." We stamp a last-activity time
 * alongside the id and treat a session idle longer than `IDLE_LIMIT_MS` as
 * expired — `getOrCreateSessionId` mints a fresh one rather than resuming a
 * stale conversation. Activity = minting, sending a turn (`touchSession`), or
 * restoring a session from history; a bare reload does NOT count, so the clock
 * tracks real conversation, not page loads. A fresh interactive sign-in clears
 * every stored session outright (`clearAllSessions`).
 */

import { newSessionId } from './agentStream.ts';
import type { AgentSlug } from './registry.ts';

const SESSION_PREFIX = 'om.session.';
/**
 * Companion key holding the session's last-activity epoch-ms. Prefixed by
 * `SESSION_PREFIX` too so `clearAllSessions` sweeps it along with the id.
 */
const ACTIVITY_PREFIX = 'om.session.activity.';

/** A session untouched for longer than this is expired (12 hours). */
const IDLE_LIMIT_MS = 12 * 60 * 60 * 1000;

/** Read a key, treating an unavailable store or read error as "absent". */
function readItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Write a key, silently degrading when the store is unavailable or full. */
function writeItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Best-effort: a failed session-id write must not interrupt the user; the
    // turn still runs against the in-memory id, only continuity-on-reload is lost.
  }
}

/** Stamp this agent's session as active now. */
function writeActivity(agentSlug: AgentSlug): void {
  writeItem(ACTIVITY_PREFIX + agentSlug, String(Date.now()));
}

/**
 * True only when a last-activity stamp exists and is within `IDLE_LIMIT_MS`.
 * A missing or unparseable stamp reads as expired (conservative: an id from
 * before this stamping existed, or a corrupted store, starts fresh rather than
 * resuming a conversation of unknown age).
 */
function isWithinIdleLimit(agentSlug: AgentSlug): boolean {
  const raw = readItem(ACTIVITY_PREFIX + agentSlug);
  if (raw === null) {
    return false;
  }
  const last = Number.parseInt(raw, 10);
  if (!Number.isFinite(last)) {
    return false;
  }
  return Date.now() - last <= IDLE_LIMIT_MS;
}

/**
 * Validate a stored id against the shape `newSessionId` mints and the runtime's
 * 33–100 char `sessionId` contract: `<agentSlug>-<hex>`, agent-prefixed, length
 * in range. Rejecting a malformed/foreign value forces a fresh mint, so a
 * corrupted store can never send the runtime an out-of-contract id.
 */
function isValidSessionId(agentSlug: AgentSlug, value: string): boolean {
  return (
    value.startsWith(`${agentSlug}-`) && value.length >= 33 && value.length <= 100
  );
}

/**
 * The persisted session id for this agent, or a freshly minted (and persisted)
 * one when none is stored or the stored value is invalid. Reuse across mounts
 * is what gives the agent conversational continuity over a reload.
 */
export function getOrCreateSessionId(agentSlug: AgentSlug): string {
  const stored = readItem(SESSION_PREFIX + agentSlug);
  if (stored !== null && isValidSessionId(agentSlug, stored) && isWithinIdleLimit(agentSlug)) {
    return stored;
  }
  const fresh = newSessionId(agentSlug);
  writeItem(SESSION_PREFIX + agentSlug, fresh);
  writeActivity(agentSlug);
  return fresh;
}

/**
 * Bump this agent's session activity stamp to now — call when a turn is sent so
 * an actively-used conversation never trips the idle limit. A bare reload (which
 * only reads via `getOrCreateSessionId`) deliberately does NOT bump, so the
 * clock measures real conversation rather than page loads.
 */
export function touchSession(agentSlug: AgentSlug): void {
  writeActivity(agentSlug);
}

/**
 * Mint, persist, and return a brand-new session id for this agent, replacing
 * any stored one. Backs an explicit "New session" action — the user choosing
 * to start a fresh conversation that the agent treats as unrelated to the last.
 */
export function startNewSession(agentSlug: AgentSlug): string {
  const fresh = newSessionId(agentSlug);
  writeItem(SESSION_PREFIX + agentSlug, fresh);
  writeActivity(agentSlug);
  return fresh;
}

/**
 * Persist an existing session id as this agent's current one — backs restoring
 * a session from the history list (the next turn resumes against it). Validates
 * the id against the same contract `getOrCreateSessionId` enforces, so a
 * malformed/foreign id is rejected (returns false, store untouched) rather than
 * sent to the runtime out of contract.
 */
export function setSessionId(agentSlug: AgentSlug, id: string): boolean {
  if (!isValidSessionId(agentSlug, id)) {
    return false;
  }
  writeItem(SESSION_PREFIX + agentSlug, id);
  writeActivity(agentSlug);
  return true;
}

/**
 * Drop every persisted session id and activity stamp. Backs a fresh interactive
 * sign-in: re-authenticating with a password (vs. a silent token refresh or a
 * reload of an existing session) starts every agent from a brand-new session,
 * so a new login never resumes the prior login's conversation. Best-effort —
 * a disabled/throwing store leaves the next mount to mint fresh ids anyway.
 */
export function clearAllSessions(): void {
  try {
    const store = window.localStorage;
    const stale: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key !== null && key.startsWith(SESSION_PREFIX)) {
        stale.push(key);
      }
    }
    for (const key of stale) {
      store.removeItem(key);
    }
  } catch {
    // Best-effort: an unavailable store has nothing to clear.
  }
}
